import React, { useRef, useEffect } from "react"
import { Link } from "gatsby"
import Slider from "react-slick"
import Anime from "react-anime"

// Import css files
import "slick-carousel/slick/slick.css"
import "slick-carousel/slick/slick-theme.css"

export default function HeroHome() {
  const pictureRef = useRef(null)
  const pictureRef2 = useRef(null)
  const sliderWrapRef = useRef(null)

  // react-slick marks cloned/inactive slides with aria-hidden="true" but leaves
  // their interactive children focusable. A MutationObserver re-syncs tabindex
  // whenever a slide's aria-hidden actually changes (covers init, autoplay, clones,
  // and manual navigation) so no focusable element remains inside an aria-hidden
  // slide (WCAG 4.1.2 Name, Role, Value / axe "aria-hidden-focus").
  useEffect(() => {
    const root = sliderWrapRef.current
    if (!root) return

    const sync = () => {
      root.querySelectorAll(".slick-slide").forEach(slide => {
        const hidden = slide.getAttribute("aria-hidden") === "true"
        slide
          .querySelectorAll("a, button, input, select, textarea, [tabindex]")
          .forEach(el => {
            const desired = hidden ? -1 : 0
            if (el.tabIndex !== desired) el.tabIndex = desired
          })
        // react-slick makes each slide's content wrapper focusable (tabindex="0")
        // but leaves it a bare <div> with no role. Give those focusable wrappers a
        // slide role so a focusable element is never left role-less (axe
        // "focusable-element" / WCAG 4.1.2).
        // react-slick can add tabindex to more than one wrapper depth
        // (e.g. .slick-current > div > div), so match every focusable bare
        // <div> in the slide, not just the direct child.
        slide.querySelectorAll("div[tabindex]").forEach(el => {
          if (!el.getAttribute("role")) {
            el.setAttribute("role", "group")
            el.setAttribute("aria-roledescription", "slide")
          }
        })
      })
    }

    sync()
    // Setting tabindex does not change aria-hidden, so observing only aria-hidden
    // avoids any feedback loop.
    const observer = new MutationObserver(mutations => {
      if (mutations.some(m => m.attributeName === "aria-hidden")) sync()
    })
    observer.observe(root, {
      attributes: true,
      attributeFilter: ["aria-hidden"],
      subtree: true,
    })
    return () => observer.disconnect()
  }, [])

  var settings = {
    dots: true,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    speed: 1000,
    autoplaySpeed: 10000,
    cssEase: "linear",
    pauseOnHover: true,
  }
  return (
    <section
      ref={sliderWrapRef}
      class="o-content-slider"
      aria-roledescription="carousel"
      aria-label="Featured highlights"
    >
      <div id="heroslider" class="o-media-hero relative">
        <div class="o-media-slider">
          <Slider {...settings}>
            <div>
              <Anime
                easing="easeOutExpo"
                duration={1200}
                direction="alternate"
                loop={false}
                delay={(el, index) => index * 240}
                translateX="0rem"
                scale={[1.6, 1]}
                opacity={[0, 1]}
                update={anime => {
                  pictureRef2.current.style.filter = "blur(" + (2000 * anime.progress) / 0 + "px)"
                }}
              >
                <div class="hero--overlay"></div>
                <img
                  ref={pictureRef2}
                  class="image-cover"
                  src="/images/slider/01.jpg"
                  alt=""
                />
              </Anime>
              <div class="o-media-hero_overlay">
                <div class="o-media-hero_caption">
                  <div class="o-media-hero_caption_balance">
                    <div class="container">
                      <div class="o-cards-view__container o-cards-view__container-xs">
                        <div class="o-cards-view__info apricus-main-title">
                          <h1 class="o-cards-view__info--heading o-cards-view__info--heading-xs">
                            Sunshine For Growing Your Wealth
                          </h1>
                          <p class="o-cards-view__info--description o-cards-view__info--description-xs">
                            Wealth Management | Investment Advisory
                          </p>
                          <div class="o-cards-view__info--cta o-cards-view__info--cta-sm mt-3">
                            <Link
                              to="/services/offerings"
                              className={
                                "c-button c-button-sm c-button-radius c-button--blue c-button-inline c-button-md"
                              }
                            >
                              Offerings
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <Anime
                easing="easeOutExpo"
                duration={1200}
                direction="alternate"
                loop={false}
                delay={(el, index) => index * 240}
                translateX="0rem"
                scale={[1.6, 1]}
                opacity={[0, 1]}
                update={anime => {
                  pictureRef.current.style.filter = "blur(" + (2000 * anime.progress) / 0 + "px)"
                }}
              >
                <div class="hero--overlay"></div>
                <img
                  ref={pictureRef}
                  class="image-cover"
                  src="/images/slider/02.jpg"
                  alt=""
                />
              </Anime>
              <div class="o-media-hero_overlay">
                <div class="o-media-hero_caption">
                  <div class="o-media-hero_caption_balance">
                    <div class="container">
                      <div class="o-cards-view__container o-cards-view__container-xs">
                        <div class="o-cards-view__info apricus-main-title">
                          <h1 class="o-cards-view__info--heading o-cards-view__info--heading-xs">
                            5 Minute Financial Health Check
                          </h1>
                          <p class="o-cards-view__info--description o-cards-view__info--description-xs">
                            Understand your financial health condition to start your investment
                            journey
                          </p>
                          <div class="o-cards-view__info--cta o-cards-view__info--cta-sm mt-3">
                            <Link
                              to="/resources/tools/5-minute-health-check"
                              className={
                                "c-button c-button-sm c-button-radius c-button--blue c-button-inline c-button-md"
                              }
                            >
                              Health Check
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Slider>
        </div>
      </div>
    </section>
  )
}
